
const REQUEST_LIMIT = 0;    // 0 for no effect, may be use for testing to limit results returned

class Query {
    constructor(uri) {
        // uri: address of server
        this.uri = uri;
    }

    neo4j(query, params, onResult, cacheKey) {
        // query: string
        // params: Object with params as properties
        // onResult: function(error, data)
        // cacheKey: store/retrieve results with this key - if absent then the cache will be ignored
        let key = "_aspace_" + cacheKey;
        if (cacheKey && localStorage[key]) {
            let resultFromCache = JSON.parse(localStorage[key]);
            if (!resultFromCache.error) {
                onResult(resultFromCache.error, resultFromCache.data);
                return;
            }
        }
        //TODO: actually check for changes before using cache

        if (REQUEST_LIMIT) query += " limit " + REQUEST_LIMIT;
        d3.json(this.uri)
            //TODO When the landing page has been created, pass authentication detail to use in Query constructor
            // Base64 of Neo4j:Anz@1016 (current test authentication) - can change in Neo4j browser using :server change-password
            .header("Authorization", "Basic bmVvNGo6QW56QDEwMTY=")
            .post(JSON.stringify({query, params}), (error, data) => {
                if (cacheKey) {
                    localStorage[key] = JSON.stringify({
                        timestamp: Date.now(),
                        uri: this.uri,
                        query,
                        params,
                        error,
                        data
                    });
                }
                onResult(error, data);
            })
        ;
    }

    //TODO: this is redundant when we are using getSpaceNodeData - remove it
    getSpaceNames(onResult) {
        this.neo4j(
            "match (n:SpaceNode) return n.name",
            {},
            (error, data) => onResult(_(data.data).map(function(d) { return d[0]; } )),
            "spaceNodeNames"
        );
    }

    promiseSpaceNodeData() {
        return new Promise((resolve, reject) => {
            // resolve: {name: {colour, paths},...}
            // reject: error
            this.neo4j(
                `match
                (n:SpaceNode),
                p = shortestPath((n)-[*]-(m:SpaceNode))
            where
                n.name <> m.name
                and reduce(result = True, n in nodes(p) | result and ('SpaceNode' in labels(n)))
            return
                n.name,
                n.colour,
                collect({path: extract(s in nodes(p) | s.name)})`,
                {},
                (error, data) => {
                    if (error) {
                        reject(error);
                        return;
                    }

                    resolve(data.data.map(d => ({
                        name: d[0],
                        colour: d[1],
                        paths: d[2].map(d => d.path)
                    })));
                },
                "spaceNodeData"
            );
        })
    }

    promiseHierarchyData(attribute) {
        // attribute: name of an attribute of type "hierarchy"
        return new Promise((resolve, reject) => {
            // resolve: {name, hierarchy}
            // reject: error
            this.neo4j(
                `match
                    (a:SpaceAttribute {name: {name}})-[:exists_in]->(root:Hierarchy),
                    p = (root)-[:has_child*]->(leaf)
                where
                    not 'Hierarchy' in labels(leaf)
                return
                    extract(n in nodes(p) | n.name) as path,
                    id(leaf) as hierarchy_id`,
                {name: attribute},
                (error, data) => {
                    if (error) {
                        reject(error);
                        return;
                    }

                    let top = [];
                    for (let d of data.data) {
                        let children = top;
                        let path = d[0];
                        for (let j in path) {
                            let childIndex = children.map(d => d.name).indexOf(path[j]);
                            if (childIndex < 0) {
                                childIndex = children.length;
                                let child = {
                                    name: path[j],
                                    children: []
                                };
                                // If this is a leaf, assign the id
                                if (+j === path.length - 1) {
                                    child.hierarchy_id = d[1];
                                }
                                children.push(child);
                            }
                            children = children[childIndex].children;
                        }
                    }
                    // Properly form into a d3-friendly hierarchy object
                    if (top.length > 1) {
                        resolve({
                            name: attribute,
                            hierarchy: {
                                root: {
                                    name: "root",
                                    children: top
                                }
                            }
                        });
                    }
                    else {
                        resolve({
                            name: attribute,
                            hierarchy: {
                                root: top[0]
                            }
                        });
                    }
                },
                attribute + "_hierarchyData"
            );
        })
    }

    promiseSpaceAttributeData(name) {
        return new Promise((resolve, reject) => {
            // resolve: [{name, type, hierarchyRoot?},...], hierarchyAttributes)
            // reject: error
            this.neo4j(
                `match (n:SpaceNode { name: {name} })-[:has]->(a:SpaceAttribute)
                return n.name as node, a.name, a.pretty, a.type
                union
                match (n:SpaceNode { name: {name} })--(m:SpaceNode)-[:has]->(a:SpaceAttribute)
                return m.name as node, a.name, a.pretty, a.type`,
                {name},
                (error, data) => {
                    if (error) {
                        reject(error);
                        return;
                    }

                    let attributes = [];
                    let aggNodes = d3.set();
                    for (let d of data.data) {
                        if (d[0] === name) {
                            // Regular attributes
                            attributes.push({
                                node: d[0],
                                name: d[1],
                                pretty: d[2],
                                type: d[3],
                                hidden: false
                            });
                        }
                        else {
                            // Get the aggregates where relevant
                            if (!aggNodes.has(d[0])) {
                                aggNodes.add(d[0]);
                                attributes.push({
                                    node: d[0],
                                    name: d[0],
                                    pretty: "Count",
                                    type: "count",
                                    hidden: false
                                });
                            }
                            if (d[3] === "currency") {		//TODO: include other summable types, e.g. people in BU
                                attributes.push({
                                    node: d[0],
                                    name: d[1],
                                    pretty: "Sum of " + d[2],
                                    type: "sum_currency",
                                    hidden: false
                                });
                            }
                        }
                    }
                    resolve(attributes);
                },
                name + "_spaceAttributeData"
            );
        })
    }

    promisePointData(name, attributes) {
        return new Promise((resolve, reject) => {
            // resolve:
            // reject: error
            let queryMatch = `match (n:${name}) `;
            let queryReturn = "return id(n) as neo4jId";
            let nodes = d3.set([name]);
            for (let a of attributes) {
                if (!nodes.has(a.node)) {
                    nodes.add(a.node);
                    queryMatch += (`, (n)--(o${a.node}:${a.node}) `);
                }
                switch (a.type) {
                    //TODO: add other aggregates, like avg, med, sd?
                    case "count":
                        queryReturn += `, count(o${a.node})`;
                        break;
                    case "sum_currency":
                        queryReturn += `, sum(o${a.node}.${a.name})`;
                        break;
                    // Hierarchy types need to match with the corresponding leaf in the attribute's hierarchy model
                    case "hierarchy":
                        queryReturn += ", id(n) as hierarchy_id";
                        break;
                    default:
                        queryReturn += ", n." + a.name;
                }
            }
            attributes.unshift({
                name: "neo4jId",
                pretty: "Neo4j ID",
                node: name,
                type: "unique",
                hidden: true
            });
            this.neo4j(
                queryMatch + queryReturn,
                {},		//TODO: use params properly
                (error, data) => {
                    if (error) {
                        reject(error);
                        return;
                    }
                    resolve(data.data);
                },
                name + "_data"
            );
        })
    }

    promiseRelationshipData(name, paths) {
        return new Promise((resolve, reject) => {
            // resolve: {nameNode: [idThere, [idHere, ...]]}
            // reject: error
            let query = paths.map(path => {
                let pathString = `(n:${path[0]})`
                        + path.slice(1, -1).map(d => `--(:${d})`).join("")
                        + `--(m:${path[path.length - 1]})`
                    ;
                return `match ${pathString} return distinct '${path[path.length - 1]}' as name, id(m), collect(distinct id(n)) `
                    ;
            }).join(" union ");

            this.neo4j(
                query,
                {},
                (error, data) => {
                    if (error) {
                        reject(error);
                        return;
                    }
                    let relationships = {};
                    for (let d of data.data) {
                        if (!relationships[d[0]]) {
                            relationships[d[0]] = [];
                        }
                        relationships[d[0]].push([d[1], d[2]]);
                    }

                    resolve(relationships);
                },
                name + "_relationships"
            );
        })
    }

}

